/**
 * If you are modifying @pushpay/link the same changes may also need to be applied to @pushpay/link-old
 */
import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { clsx, createUseStyles } from '@pushpay/styles';
import { isGivingOldTheme } from '@pushpay/theming';
import { forwardRef } from '@pushpay/types';
const useLinkStylesInternal = createUseStyles(theme => ({
  root: {},
  active: {},
  text: {
    font: theme.TYPOGRAPHY.BODY_1,
    color: isGivingOldTheme(theme) ? theme.colors['palette-blue'] : theme.colors['product-color'],
    textDecoration: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '&:hover, &:focus': {
      textDecoration: 'underline'
    }
  },
  unstyled: {
    font: 'inherit',
    color: 'inherit',
    textDecoration: 'none'
  }
}));
export const useLinkStyles = () => useLinkStylesInternal(undefined);
/**
 * Will enhance the string provided as rel attribute on link with required values if target is specified
 * @param target link target attribute
 * @param rel link rel attribute
 * @returns the computed rel attribute value guaranteed to contain 'noreferrer noopener' if target is specified
 */

function computeRelAttribute(target, rel) {
  if (!target) {
    return rel;
  }

  const definedRelParts = rel ? rel.split(' ') : [];
  const requiredRelParts = ['noreferrer', 'noopener'];
  const uniqueRelParts = Array.from(new Set([...definedRelParts, ...requiredRelParts]));
  return uniqueRelParts.join(' ');
}

export const Link = forwardRef(function Link(props, ref) {
  const {
    classes: classesProp,
    className: classNameProp,
    children,
    href,
    to,
    target,
    rel,
    end = false,
    displayStyle = 'text',
    'aria-current': ariaCurrentProp = 'page',
    ...rest
  } = props;
  const classes = useLinkStylesInternal(classesProp);
  const computedRel = useMemo(() => computeRelAttribute(target, rel), [target, rel]);
  const className = clsx(classes.root, classNameProp, classes[displayStyle]);

  if (to) {
    return /*#__PURE__*/React.createElement(NavLink, Object.assign({
      ref: ref,
      to: to,
      end: end,
      "aria-current": ariaCurrentProp,
      className: ({
        isActive
      }) => clsx(className, isActive && classes.active),
      target: target,
      rel: computedRel
    }, rest), children);
  } // Non-relative links can only be active in server rendered applications eg. Platform or ChMS
  // We don't need to worry about re-rendering on route change so don't depend on useLocation which would necessitate a <Router /> higher up the tree


  function getActive() {
    try {
      if (href) {
        const locationPathname = window.location.pathname.toLowerCase();
        const hrefPathname = new URL(href.startsWith('//') ? `https:${href}` : href).pathname.toLowerCase();
        return end ? locationPathname === hrefPathname : locationPathname.startsWith(hrefPathname);
      }

      return false;
    } catch {
      return false;
    }
  }

  const isActive = getActive();
  return /*#__PURE__*/React.createElement("a", Object.assign({
    ref: ref,
    href: href,
    "aria-current": isActive ? ariaCurrentProp : undefined,
    className: clsx(className, isActive && classes.active),
    target: target,
    rel: computedRel
  }, rest), children);
});